import React, { useEffect, useState, useMemo, useRef } from 'react';
import WhiteboardLayout from './WhiteboardLayout';
import { useWebView } from '../integrations/webview';
import { useTestStore } from '../store/useTestStore';
import { buildUIConfigFromWebView } from '../utils/testUIConfigBuilder';
import { useUserStore } from '../store/useUserStore';
import { useAuthStore } from '../store/useAuthStore';
import { useTenantStore } from '../store/useTenantStore';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
/**
 * Wrapper component that provides WebView data to WhiteboardLayout
 */
export const WebViewWhiteboardWrapper: React.FC = () => {
  const { isWebView, externalProps, isLoading } = useWebView();
  const { setQuestions, setGroups, setCurrentTestId, setTestTitle, setTotalTime, clearAnswers, setCurrentQuestionIndex } = useTestStore();
  const userInfo = useUserStore((state) => state.userInfo);
  const setUserInfo = useUserStore((state) => state.setUserInfo);
  const { setAccessToken } = useAuthStore();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasStartedLoadingRef = useRef(false);
  const lastStudyTaskIdRef = useRef<string | null>(null);
  
  // Debug log to see current state
  useEffect(() => {
    console.log('🔍 WebViewWhiteboardWrapper - Current state:', {
      isWebView,
      hasExternalProps: !!externalProps,
      isLoading,
      dataLoaded,
      hasStartedLoading: hasStartedLoadingRef.current,
      externalPropsKeys: externalProps ? Object.keys(externalProps) : []
    });
  }, [isWebView, externalProps, isLoading, dataLoaded]);

  useEffect(() => {
    // Skip if not in WebView mode or no external props
    if (!isWebView || !externalProps) {
      return;
    }
    
    // Check if study task has changed
    const currentStudyTaskId = externalProps.studyTask?.id || externalProps.studyTask?.resourceId || null;
    const hasStudyTaskChanged = currentStudyTaskId !== lastStudyTaskIdRef.current;
    
    // If study task changed, reset loading state
    if (hasStudyTaskChanged && currentStudyTaskId) {
      console.log('🔄 Study task changed from', lastStudyTaskIdRef.current, 'to', currentStudyTaskId);
      console.log('🧹 Will clear all data and reload questions for new study task');
      
      // Clear all existing data immediately
      setQuestions([]);
      setGroups([]);
      setCurrentTestId(null);
      setTestTitle('');
      setTotalTime(undefined);
      clearAnswers();
      setCurrentQuestionIndex(0);
      
      // Clear whiteboard store data
      const whiteboardStore = useWhiteboardStore.getState();
      whiteboardStore.setQuestionDataCache(new Map());
      whiteboardStore.clearOpticalAnswers();
      whiteboardStore.clearStrokes();
      
      // Reset loading state
      hasStartedLoadingRef.current = false;
      setDataLoaded(false);
      setError(null);
      lastStudyTaskIdRef.current = currentStudyTaskId;
    }
    
    // Skip if already loading or loaded (but allow if study task changed)
    if (hasStartedLoadingRef.current || (dataLoaded && !hasStudyTaskChanged)) {
      return;
    }
    
    // Mark as started immediately
    hasStartedLoadingRef.current = true;
    
    console.log('🔍 WebViewWhiteboardWrapper - Initializing once');
    
    // Process auth info
    if (externalProps.authConfig) {
      // Set user info
      if (externalProps.authConfig.userInfo) {
        const rawUserId = externalProps.authConfig.userInfo.id;
        console.log('🔍 Initial userInfo setup:', {
          incomingId: rawUserId,
          idType: typeof rawUserId,
          idLength: typeof rawUserId === 'string' ? rawUserId.length : 'N/A',
          idValue: JSON.stringify(rawUserId),
          fullUserInfo: JSON.stringify(externalProps.authConfig.userInfo)
        });
        
        // Double check the userId is not being truncated
        if (typeof rawUserId === 'string' && rawUserId.length < 10) {
          console.error('⚠️ WARNING: userId appears to be truncated!', rawUserId);
        }
        
        setUserInfo({
          id: rawUserId as any, // id can be string or number
          userName: externalProps.authConfig.userInfo.userName || externalProps.authConfig.userInfo.name,
          name: externalProps.authConfig.userInfo.name,
          surname: '',
          emailAddress: externalProps.authConfig.userInfo.email,
          isActive: true,
          fullName: externalProps.authConfig.userInfo.name,
          lastLoginTime: null,
          creationTime: new Date(),
          roleNames: externalProps.authConfig.userInfo.roles || 
                     (externalProps.authConfig.userInfo.role ? [externalProps.authConfig.userInfo.role] : ['kullanici']),
        });
        console.log('✅ Initial userInfo set with id:', rawUserId);
        
        // Verify it was set correctly
        setTimeout(() => {
          const currentUserInfo = useUserStore.getState().userInfo;
          console.log('🔍 Verifying userInfo after set:', {
            currentId: currentUserInfo?.id,
            currentIdType: typeof currentUserInfo?.id,
            matches: currentUserInfo?.id === rawUserId
          });
        }, 100);
      }
      
      // Set access token
      if (externalProps.authConfig.accessToken) {
        setAccessToken(externalProps.authConfig.accessToken, 86400); // 24 hours
      }
      
      // Set tenant ID - default to 50000 for WebView
      const tenantStore = useTenantStore.getState();
      const tenantId = Number(externalProps.authConfig.tenantId) || 50000;
      // For WebView, we don't have serverRootAddress, use API root
      const serverRoot = externalProps.authConfig.firebaseAuthRootUrl || 'https://api.sincappai.com';
      tenantStore.setTenant(tenantId, serverRoot);
      console.log('Set tenant ID:', tenantId, 'Server root:', serverRoot);
    }
    
    // Handle data loading
    const loadData = async () => {
      // Clear existing questions and groups before loading new ones
      console.log('🧹 Clearing existing questions and groups');
      console.log('📊 Before clearing - Questions count:', useTestStore.getState().questions.length);
      setQuestions([]);
      setGroups([]);
      setCurrentTestId(null);
      setTestTitle('');
      setTotalTime(undefined);
      
      // Clear whiteboard store question data cache
      const whiteboardStore = useWhiteboardStore.getState();
      console.log('📊 Before clearing - Cached question data:', whiteboardStore.questionDataCache.size);
      whiteboardStore.setQuestionDataCache(new Map());
      whiteboardStore.clearOpticalAnswers();
      whiteboardStore.clearStrokes();
      
      // Clear test store answers
      clearAnswers();
      // NOTE: Don't reset currentQuestionIndex here - it will be restored by restoreTestState()
      console.log('📊 After clearing - Questions count:', useTestStore.getState().questions.length);
      
      // Fetch questions from API if studyTask is provided
      if (externalProps.studyTask) {
        console.log('📡 Starting API call for questions');
        setQuestionsLoading(true);
        
        try {
          const apiUrl = externalProps.authConfig?.testApiUrl || 'https://api.sincappai.com/api/app/study-action/exam-questions';
          
          // Ensure all required parameters have default values
          const completeStudyTask = {
            ...externalProps.studyTask,
            params: {
              // Set defaults for all parameters
              nQuestions: 0,
              totalTime: 0,
              sessionType: 0,
              examType: 0,
              testType: 0,
              scoreType: 0,
              daysInDeneme: 0,
              timePerQuestion: 0,
              questionTimeLimit: 0,
              allowBackNavigation: false,
              showQuestionTimer: false,
              // Override with actual values from externalProps
              ...externalProps.studyTask?.params,
              // Ensure numeric values are properly typed
              ...(externalProps.studyTask?.params && {
                nQuestions: Number(externalProps.studyTask.params.nQuestions || 0),
                totalTime: Number(externalProps.studyTask.params.totalTime || 0),
                sessionType: Number(externalProps.studyTask.params.sessionType || 0),
                examType: Number(externalProps.studyTask.params.examType || 0),
                testType: Number(externalProps.studyTask.params.testType || 0),
                scoreType: Number(externalProps.studyTask.params.scoreType || 0),
                daysInDeneme: Number(externalProps.studyTask.params.daysInDeneme || 0),
                timePerQuestion: Number(externalProps.studyTask.params.timePerQuestion || 0),
                questionTimeLimit: Number(externalProps.studyTask.params.questionTimeLimit || 0),
                allowBackNavigation: Boolean(externalProps.studyTask.params.allowBackNavigation),
                showQuestionTimer: Boolean(externalProps.studyTask.params.showQuestionTimer)
              })
            }
          };
          
          console.log('🚀 Fetching questions from API:', apiUrl);
          console.log('📋 Study Task being sent:', JSON.stringify(completeStudyTask, null, 2));
          console.log('🔑 Access Token:', externalProps.authConfig.accessToken ? 'Present' : 'Missing');
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'accept': 'text/plain',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${externalProps.authConfig.accessToken}`,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(completeStudyTask),
            mode: 'cors',
            credentials: 'include'
          });
          
          if (!response.ok) {
            // Try to get error details from response body
            let errorMessage = `API Error: ${response.status} ${response.statusText}`;
            try {
              const errorBody = await response.text();
              if (errorBody) {
                console.error('API Error Response Body:', errorBody);
                errorMessage += ` - ${errorBody}`;
              }
            } catch (e) {
              console.error('Could not parse error response body');
            }
            throw new Error(errorMessage);
          }
          
          const data = await response.json();
          console.log('✅ API Response received:', {
            hasCoursesGroups: !!data.coursesGroups,
            coursesGroupsLength: data.coursesGroups?.length,
            allKeys: Object.keys(data),
            dataStructure: data
          });
          
          // Set userId from studyTask.userAccountId
          const userId = data.studyTask?.userAccountId || externalProps.studyTask?.userAccountId || externalProps.authConfig?.userInfo?.id;
          console.log('🔍 Setting userId from API response:', {
            fromDataStudyTask: data.studyTask?.userAccountId,
            fromExternalStudyTask: externalProps.studyTask?.userAccountId,
            fromAuthConfig: externalProps.authConfig?.userInfo?.id,
            finalUserId: userId,
            currentUserInfo: userInfo
          });
          if (userId && userInfo) {
            setUserInfo({
              ...userInfo,
              id: userId,
            });
            console.log('✅ UserInfo updated with userId:', userId);
          }
          
          // Set test metadata from API response
          const apiStudyTask = data.studyTask || externalProps.studyTask;
          if (apiStudyTask) {
            // IMPORTANT: Always use resourceId as testId for consistency
            // This ensures answers are saved and loaded with the same ID
            const testId = apiStudyTask.resourceId || apiStudyTask.id;
            setCurrentTestId(testId);
            setTestTitle(apiStudyTask.title || '');
            
            if (apiStudyTask.params?.totalTime) {
              setTotalTime(apiStudyTask.params.totalTime);
            }
            
            console.log('📝 Setting test metadata:', {
              testId,
              studyTaskId: apiStudyTask.id,
              resourceId: apiStudyTask.resourceId,
              title: apiStudyTask.title,
              willUseAsTestId: testId
            });
            
            // Warn if studyTask.id and resourceId are different
            if (apiStudyTask.id && apiStudyTask.resourceId && apiStudyTask.id !== apiStudyTask.resourceId) {
              console.warn('⚠️ StudyTask has different id and resourceId:', {
                id: apiStudyTask.id,
                resourceId: apiStudyTask.resourceId,
                usingAsTestId: testId
              });
            }
          }
          
          // Set courses groups from API response
          if (data.coursesGroups && data.coursesGroups.length > 0) {
            console.log('Setting courses groups from API:', data.coursesGroups);
            console.log('First group structure:', {
              groupName: data.coursesGroups[0].groupName,
              coursesCount: data.coursesGroups[0].courses?.length || 0,
              firstCourse: data.coursesGroups[0].courses?.[0]
            });
            setGroups(data.coursesGroups);
            
            // Extract and transform questions from coursesGroups
            const allQuestions = data.coursesGroups
              .flatMap((group: any) => 
                (group.courses || []).flatMap((course: any) => 
                  (course.questions || []).map((q: any) => ({
                    // Map API fields to expected structure
                    id: q.questionId,
                    questionId: q.questionId,
                    title: `Soru ${q.questionOrder}`,
                    imageUrl: q.imageUrl,
                    pdfUrl: q.pdfUrl,
                    answer: q.answer,
                    questionOrder: q.questionOrder,
                    courseName: course.courseName,
                    courseId: course.courseId,
                    groupName: group.groupName,
                    groupId: group.groupId,
                    href: q.href,
                    answerType: q.answerType,
                    questionProperty: q.questionProperty,
                    type: 'question' // Add default type
                  }))
                )
              );
              
            if (allQuestions.length > 0) {
              console.log('Extracted questions from API:', allQuestions.length);
              console.log('First question sample:', {
                id: allQuestions[0].id,
                title: allQuestions[0].title,
                imageUrl: allQuestions[0].imageUrl,
                courseName: allQuestions[0].courseName
              });
              setQuestions(allQuestions);

              console.log('✅ [WebView] Questions set, WhiteboardProvider will handle restore via restoreTestState');

              // Also check if questions are in store after setting
              setTimeout(() => {
                const storeState = useTestStore.getState();
                console.log('Questions in store:', storeState.questions.length);
                console.log('Groups in store:', storeState.groups.length);
                console.log('First question in store:', storeState.questions[0]);
              }, 100);
              
              // Check again after a longer delay to see if they're being reset
              setTimeout(() => {
                const storeState = useTestStore.getState();
                console.log('🔍 After 2 seconds - Questions in store:', storeState.questions.length);
                console.log('🔍 After 2 seconds - Groups in store:', storeState.groups.length);
                if (storeState.questions.length === 0 || (storeState.questions.length === 1 && storeState.questions[0].questionId === 'blank-question-00000000-0000-0000-0000-000000000000')) {
                  console.error('❌ Questions were reset by another component!');
                }
              }, 2000);
            }
          }
          
          setDataLoaded(true);
          setQuestionsLoading(false);
        } catch (err) {
          console.error('Error fetching questions:', err);
          // Log the full error details for debugging
          if (err instanceof Error) {
            console.error('Error details:', {
              message: err.message,
              stack: err.stack,
              studyTaskSent: externalProps.studyTask
            });
          }
          setError(err instanceof Error ? err.message : 'Failed to fetch questions');
          setQuestionsLoading(false);
          setDataLoaded(true); // Still mark as loaded to prevent retries
        }
      } else {
        // If no studyTask but coursesGroups provided (backward compatibility)
        if (externalProps.coursesGroups && externalProps.coursesGroups.length > 0) {
          console.log('Setting courses groups from externalProps:', externalProps.coursesGroups);
          setGroups(externalProps.coursesGroups);
          
          // Extract questions from coursesGroups
          const allQuestions = externalProps.coursesGroups
            .flatMap((group: any) => group.courses || [])
            .flatMap((course: any) => course.questions || []);
            
          if (allQuestions.length > 0) {
            console.log('Extracted questions from coursesGroups:', allQuestions.length);
            setQuestions(allQuestions);
          }
        }
        
        // Mark data as loaded
        setDataLoaded(true);
      }
    };
    
    loadData();
  }, [isWebView, externalProps]); // Only depend on isWebView and externalProps

  // Build UI config from WebView props - memoized to prevent re-renders
  const uiConfig = useMemo(() => {
    if (isWebView && externalProps) {
      // Merge default UI config with provided one
      const defaultConfig = buildUIConfigFromWebView(externalProps, userInfo);
      return {
        ...defaultConfig,
        ...externalProps.uiConfig
      };
    }
    return undefined;
  }, [isWebView, externalProps, userInfo]);

  // Create external props for WhiteboardLayout - memoized to prevent re-renders
  const whiteboardExternalProps = useMemo(() => {
    if (isWebView && externalProps && dataLoaded) {
      // Add Firebase config if auth config is provided
      const firebaseConfig = externalProps.authConfig ? {
        tokenApiRoot: externalProps.authConfig.firebaseAuthRootUrl || 'https://api.sincappai.com',
        tokenApiPath: '/api/WhiteboardFirebase/GetToken',
        refreshTokenApiPath: '/api/WhiteboardFirebase/RefreshToken',
        getFirebaseToken: async () => {
          const apiUrl = `${externalProps.authConfig.firebaseAuthRootUrl || 'https://api.sincappai.com'}/api/WhiteboardFirebase/GetToken`;
          console.log('🔥 Getting Firebase token from:', apiUrl);
          console.log('🔥 Firebase token request userId:', {
            userId: externalProps.authConfig.userInfo?.id,
            userIdType: typeof externalProps.authConfig.userInfo?.id,
            userIdLength: typeof externalProps.authConfig.userInfo?.id === 'string' ? externalProps.authConfig.userInfo.id.length : 'N/A'
          });
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'accept': 'text/plain',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${externalProps.authConfig.accessToken}`,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              deviceId: 'webview-device-' + Date.now(),
              deviceName: 'WebView Device',
              userId: externalProps.authConfig.userInfo?.id || ''
            }),
            mode: 'cors',
            credentials: 'include'
          });
          
          if (!response.ok) {
            throw new Error(`Firebase token API error: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('✅ Firebase token received:', data);
          
          // Handle different response structures
          const result = data.result || data;
          
          return {
            firebaseToken: result.firebaseToken,
            refreshToken: result.refreshToken,
            expiresIn: result.expiresIn
          };
        },
        refreshFirebaseToken: async (refreshToken: string) => {
          const apiUrl = `${externalProps.authConfig.firebaseAuthRootUrl || 'https://api.sincappai.com'}/api/WhiteboardFirebase/RefreshToken`;
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'accept': 'text/plain',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${externalProps.authConfig.accessToken}`,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              refreshToken,
              deviceId: 'webview-device-' + Date.now()
            }),
            mode: 'cors',
            credentials: 'include'
          });
          
          if (!response.ok) {
            throw new Error(`Firebase refresh token API error: ${response.status}`);
          }
          
          const data = await response.json();
          
          // Handle different response structures
          const result = data.result || data;
          
          return {
            firebaseToken: result.firebaseToken,
            refreshToken: result.refreshToken,
            expiresIn: result.expiresIn
          };
        }
      } : undefined;
      
      // Get testId from studyTask.resourceId (consistent with test store)
      // IMPORTANT: Always use resourceId as testId for consistency
      const testId = externalProps.studyTask?.resourceId || externalProps.testConfig?.testId;
      
      console.log('🔍 WebView testId for whiteboard props:', {
        fromResourceId: externalProps.studyTask?.resourceId,
        fromTestConfig: externalProps.testConfig?.testId,
        finalTestId: testId,
        studyTaskId: externalProps.studyTask?.id
      });
      
      // Build the WhiteboardExternalProps
      const userIdForProps = externalProps.authConfig.userInfo.id;
      console.log('🔍 Building whiteboardProps with userId:', {
        originalId: userIdForProps,
        stringifiedId: String(userIdForProps),
        typeBeforeStringify: typeof userIdForProps,
        typeAfterStringify: typeof String(userIdForProps)
      });
      
      const whiteboardProps: any = {
        authConfig: {
          userInfo: {
            id: String(userIdForProps),
            name: externalProps.authConfig.userInfo.name,
            email: externalProps.authConfig.userInfo.email
          },
          tenantId: Number(externalProps.authConfig.tenantId) || 50000,
          accessToken: externalProps.authConfig.accessToken
        },
        testId,
        studyTask: externalProps.studyTask,
        uiConfig,
        firebaseConfig,
        questions: externalProps.questions,
        coursesGroups: externalProps.coursesGroups,
        dataService: externalProps.dataService,
        callbacks: externalProps.callbacks,
        options: externalProps.options,
        features: externalProps.features
      };
      
      // Add testConfig if studyTask exists
      if (externalProps.studyTask) {
        whiteboardProps.testConfig = {
          testId: testId || externalProps.studyTask.resourceId,
          studyTask: externalProps.studyTask
        };
      }
      
      return whiteboardProps;
    }
    return undefined;
  }, [isWebView, externalProps, uiConfig, dataLoaded]);

  // Show loading state while waiting for WebView data or questions
  if (isLoading || (isWebView && !externalProps) || questionsLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {questionsLoading ? 'Sorular yükleniyor...' : 'WebView verisi bekleniyor...'}
          </p>
          <p className="text-sm text-gray-500 mt-2">Lütfen bekleyin</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-800 font-semibold">Bir hata oluştu</p>
          <p className="text-gray-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  // Wait for data to be loaded
  if (!dataLoaded) {
    return null;
  }

  return <WhiteboardLayout externalProps={whiteboardExternalProps} />;
};